import { inject, Injectable, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
// NOTE FOR DEVELOPERS:
// We import Auth, signInWithEmailAndPassword, signOut, and UserCredential from 'firebase/auth' (the official Firebase JS SDK)
// instead of '@angular/fire/auth' because:
// 1. The official SDK provides direct, up-to-date access to all Firebase Auth features and types.
// 2. '@angular/fire/auth' is a thin Angular wrapper and may not always expose the latest or all features/types.
// 3. Using the official SDK ensures compatibility and avoids issues with missing or lagging type definitions in the AngularFire wrapper.
import { Auth, signInWithEmailAndPassword, signOut, UserCredential } from 'firebase/auth';
import { User } from '../models/user.model';
import { FirebaseInitService } from './../services/firebase-init.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  /**
   * Signal reflecting the current Firebase Auth user state.
   * This is updated by FirebaseInitService and should be used for all user state checks.
   */
  readonly user: WritableSignal<User | null>;
  /**
   * Signal indicating if the user is approved (from FirebaseInitService).
   */
  readonly isUserApproved: WritableSignal<boolean | null>;

  private firebaseInitService = inject(FirebaseInitService);
  private auth: Auth;
  private router = inject(Router);

  constructor() {
    this.auth = this.firebaseInitService.auth; // Get the Firebase Auth instance
    this.user = this.firebaseInitService.firebaseUser; // Assign the user signal
    this.isUserApproved = this.firebaseInitService.isUserApproved; // Assign the approval signal
  }

  /**
   * Login with Firebase Auth
   * @param email User's email
   * @param password User's password
   * @returns 'success' or error code string
   */
  async login(email: string, password: string): Promise<'success' | string> {
    try {
      // Firebase Authentication
      const credential: UserCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const uid = credential.user?.uid;
      if (!uid) throw new Error('custom/user-doc-missing');
      return 'success';
    } catch (err: any) {
      // Map Firebase errors to custom codes
      let code = err.code || err.message || 'auth/unknown';
      if (code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        return 'auth/invalid-credentials';
      }
      if (code === 'auth/network-request-failed') {
        return 'auth/network-request-failed';
      }
      return code;
    }
  }

  /**
   * Logout and clear user state. Redirects to login page after logout.
   */
  async logout(): Promise<void> {
    await signOut(this.auth);
    this.router.navigate(['/login']);
  }

  /**
   * Send a password reset email to the given address
   * @param email User's email
   * @returns 'success' or error code string
   */
  async sendPasswordResetEmail(email: string): Promise<'success' | string> {
    try {
      await import('@angular/fire/auth').then(({ sendPasswordResetEmail }) => sendPasswordResetEmail(this.auth, email));
      return 'success';
    } catch (err: any) {
      let code = err.code || err.message || 'auth/unknown';
      if (code === 'auth/user-not-found') {
        // For security, treat as success (Firebase best practice)
        return 'success';
      }
      if (code === 'auth/network-request-failed') {
        return 'auth/network-request-failed';
      }
      return code;
    }
  }
}
